import { Logo } from "@p0security/frontend-core/react";
import {
  ACCESS_TYPE_BLOCK_ID,
  RESOURCE_SELECTOR_BLOCK_ID,
} from "@p0security/shared/integrations/notifiers/constants";
import type {
  WebInputChoice,
  WebModalState,
} from "@p0security/shared/integrations/notifiers/web-requests/types";
import type { BundleItem } from "@p0security/shared/types/bundle";
import { Alert, Flex } from "@p0security/ui";
import { useBlockForm } from "components/Jit/Modal/useBlockForm";
import { accessTypeIcon } from "components/Jit/Policy/accessTypeIcons";
import { type PillSelectOption } from "components/common/PillSelect";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  HIDDEN_BLOCK_IDS,
  buildBundleItem,
  filterBundleResource,
  isValueBlock,
} from "./BundleBlockRenderers";
import { IntegrationSelector } from "./IntegrationSelector";
import { ResourceConfigurator } from "./ResourceConfigurator";

const toStringValue = (val: unknown): string | undefined => {
  if (val == null || val === "") return undefined;
  if (typeof val === "string") return val;
  if (typeof val === "object" && "value" in val)
    return String((val as WebInputChoice).value);
  return String(val);
};

const blockToPillOptions = (
  options: WebInputChoice[]
): PillSelectOption<string>[] =>
  options.map((opt) => ({
    icon: opt.icon ? <Logo logo={opt.icon} title={opt.label} /> : undefined,
    label: opt.label,
    searchText: opt.label,
    value: opt.value,
  }));

/** Reconstruct backend-facing form values from a saved BundleItem so the
 *  backend returns blocks with `value` pre-populated. Arg keys in the item
 *  are stored without the `${type}_${access}_` prefix, so we send both the
 *  prefixed and unprefixed forms — the backend matches by block ID. */
const bundleItemToValues = (item: BundleItem): WebModalState => {
  const values: WebModalState = {
    [RESOURCE_SELECTOR_BLOCK_ID]: item.type,
    [ACCESS_TYPE_BLOCK_ID]: item.access,
  };
  const prefix = `${item.type}_${item.access}_`;
  for (const [key, value] of Object.entries(item.args)) {
    values[`${prefix}${key}`] = value as string;
    values[key] = value as string;
  }
  return values;
};

const buildCurrentItem = (
  watching: Record<string, unknown>,
  blocks: ReturnType<typeof useBlockForm>["blocks"]
): BundleItem | undefined => {
  const allValues: WebModalState = {};
  for (const [key, value] of Object.entries(watching)) {
    allValues[key] =
      value &&
      typeof value === "object" &&
      "value" in value &&
      !Array.isArray(value)
        ? (value as WebInputChoice).value
        : value;
  }
  return buildBundleItem(blocks, allValues);
};

export const InlineBundleResourceEditor: React.FC<{
  initialItem?: BundleItem;
  onChange: (item: BundleItem | undefined) => void;
  onRemove: () => void;
  duplicateWarning?: string;
  preselectedIntegration?: string;
  excludeIntegrations?: string[];
  onIntegrationSelect?: (integration: string) => void;
  onAvailableIntegrations?: (keys: string[]) => void;
}> = ({
  initialItem,
  onChange,
  onRemove,
  duplicateWarning,
  preselectedIntegration,
  excludeIntegrations,
  onIntegrationSelect,
  onAvailableIntegrations,
}) => {
  const {
    blocks,
    form,
    watching,
    dispatching,
    error,
    clearError,
    handleFetchOptions,
    dispatch,
  } = useBlockForm({
    dispatchEndpoint: "bundles/resource-picker",
    suggestionsEndpoint: "bundles/resource-picker/suggestions",
    filterBlocks: filterBundleResource,
  });

  const { setValue } = form;

  useEffect(() => {
    dispatch(
      initialItem
        ? bundleItemToValues(initialItem)
        : preselectedIntegration
          ? { [RESOURCE_SELECTOR_BLOCK_ID]: preselectedIntegration }
          : {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectIntegration = useCallback(
    (value: string) => {
      setValue(RESOURCE_SELECTOR_BLOCK_ID, value, { shouldDirty: true });
      onIntegrationSelect?.(value);
    },
    [setValue, onIntegrationSelect]
  );

  const handleSelectAccessType = useCallback(
    (value: string) => {
      setValue(ACCESS_TYPE_BLOCK_ID, value, { shouldDirty: true });
    },
    [setValue]
  );

  // Fallback order matters: form value isn't populated on first render, so fall
  // through to initialItem (editing) or preselectedIntegration (new in a group)
  const selectedIntegration =
    toStringValue(watching[RESOURCE_SELECTOR_BLOCK_ID]) ??
    initialItem?.type ??
    preselectedIntegration;
  const selectedAccessType = toStringValue(watching[ACCESS_TYPE_BLOCK_ID]);

  const resourceBlock = useMemo(
    () =>
      blocks.find(
        (b) => b.id === RESOURCE_SELECTOR_BLOCK_ID && b.type === "static-select"
      ),
    [blocks]
  );

  const accessTypeBlock = useMemo(
    () =>
      blocks.find(
        (b) => b.id === ACCESS_TYPE_BLOCK_ID && b.type === "static-select"
      ),
    [blocks]
  );

  useEffect(() => {
    if (resourceBlock?.type === "static-select") {
      onAvailableIntegrations?.(resourceBlock.options.map((o) => o.value));
    }
  }, [resourceBlock, onAvailableIntegrations]);

  const integrationOptions = useMemo(
    () =>
      resourceBlock && resourceBlock.type === "static-select"
        ? blockToPillOptions(
            excludeIntegrations
              ? resourceBlock.options.filter(
                  (opt) => !excludeIntegrations.includes(opt.value)
                )
              : resourceBlock.options
          )
        : [],
    [resourceBlock, excludeIntegrations]
  );

  const accessTypeOptions = useMemo(
    () =>
      accessTypeBlock && accessTypeBlock.type === "static-select"
        ? accessTypeBlock.options.map((opt) => ({
            icon: accessTypeIcon(opt.value),
            label: opt.label,
            searchText: opt.label,
            value: opt.value,
          }))
        : [],
    [accessTypeBlock]
  );

  const accessTypeBlockHidden = useMemo(
    () => accessTypeBlock?.hidden === true,
    [accessTypeBlock]
  );

  const argBlocks = useMemo(
    () =>
      blocks.filter(
        (block) =>
          block.id !== RESOURCE_SELECTOR_BLOCK_ID &&
          block.id !== ACCESS_TYPE_BLOCK_ID &&
          !HIDDEN_BLOCK_IDS.includes(block.id)
      ),
    [blocks]
  );

  const isComplete = useMemo(() => {
    if (!selectedIntegration || !selectedAccessType) return false;
    if (
      blocks.some(
        (b) =>
          b.type === "alert" &&
          (b.variant === "error" || b.variant === "warning")
      )
    )
      return false;
    return argBlocks.every((block) => {
      if (!isValueBlock(block) || block.hidden || !block.required) return true;
      const value = watching[block.id];
      return Array.isArray(value) ? value.length > 0 : !!value;
    });
  }, [selectedIntegration, selectedAccessType, blocks, argBlocks, watching]);

  const lastPropagatedRef = useRef<BundleItem | undefined>(initialItem);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (dispatching) return;
      const item = isComplete ? buildCurrentItem(watching, blocks) : undefined;
      if (!isEqual(item, lastPropagatedRef.current)) {
        lastPropagatedRef.current = item;
        onChange(item);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isComplete, watching, blocks, dispatching, onChange]);

  return (
    <Flex vertical gap={8} className="w-full">
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={clearError}
        />
      )}

      {duplicateWarning && (
        <Alert
          message={duplicateWarning}
          type="warning"
          showIcon
          closable
          banner
        />
      )}

      <IntegrationSelector
        visible={!selectedIntegration}
        integrationOptions={integrationOptions}
        isLoading={!resourceBlock}
        selectedIntegration={selectedIntegration}
        onSelectIntegration={handleSelectIntegration}
        onRemove={onRemove}
      />

      <ResourceConfigurator
        visible={!!selectedIntegration}
        accessTypeOptions={accessTypeOptions}
        accessTypeBlockHidden={accessTypeBlockHidden}
        selectedAccessType={selectedAccessType}
        onSelectAccessType={handleSelectAccessType}
        argBlocks={argBlocks}
        form={form}
        onFetchOptions={handleFetchOptions}
        watching={watching}
        dispatching={dispatching}
        isComplete={isComplete}
        onRemove={onRemove}
      />
    </Flex>
  );
};
